import type { Element } from '@xmpp/xml'
import type { Client } from '@xmpp/client'
import { Affiliations, Marker } from '../chat-data/types'
import { parseFinInboxStanza, parseInboxStanza } from './parsers/inboxStanza'
import {
  parseArchivedGroupchatMarkerStanza,
  parseGroupchatMarkerStanza,
} from './parsers/groupchatMarkerStanza'
import {
  parseAffiliationsStanza,
  parseArchivedAffiliationsStanza,
  parseArchivedGroupchatStanza,
  parseFailedGroupchatStanza,
  parseGroupchatStanza,
} from './parsers/groupchatStanza'
import { PayloadIn } from './types'
import { parseConversationStatusChangeStanza } from './parsers/statusStanza'

export const createStanzaListener = <T>(
  client: Client,
  callback: (cb: T) => void,
  parser: (stanza: Element) => T | undefined
) => {
  const onStanza = (stanza: Element) => {
    const result = parser(stanza)
    if (result) {
      callback(result)
    }
  }

  client.on('stanza', onStanza)
  return () => {
    client.removeListener('stanza', onStanza)
  }
}

export const onInboxStanza = (
  client: Client,
  callback: (event: NonNullable<ReturnType<typeof parseInboxStanza>>) => void
) => {
  return createStanzaListener(client, callback, parseInboxStanza)
}

export const onFinInboxStanza = (
  client: Client,
  callback: (event: { lastId: string; queryId: string }) => void
) => {
  return createStanzaListener(client, callback, parseFinInboxStanza)
}

export const onArchiveGroupchatStanza = (
  client: Client,
  callback: (event: PayloadIn) => void,
  filter: (stanza: Element) => boolean
) => {
  return createStanzaListener(client, callback, element => {
    if (!filter(element)) {
      return
    }
    return parseArchivedGroupchatStanza(element)
  })
}

export const onArchivedAffiliationsStanza = (
  client: Client,
  callback: (event: Affiliations) => void,
  filter?: (stanza: Element) => boolean
) => {
  return createStanzaListener(client, callback, element => {
    if (filter && !filter(element)) {
      return
    }
    return parseArchivedAffiliationsStanza(element)
  })
}

export const onGroupchatStanza = (client: Client, callback: (event: PayloadIn) => void) => {
  return createStanzaListener(client, callback, parseGroupchatStanza)
}

export const onGroupchatFailedStanza = (
  client: Client,
  callback: (event: Extract<PayloadIn, { type: 'Error' }>) => void
) => {
  return createStanzaListener(client, callback, parseFailedGroupchatStanza)
}

export const onAffiliationsStanza = (client: Client, callback: (data: Affiliations) => void) => {
  return createStanzaListener(client, callback, parseAffiliationsStanza)
}

export const onArchivedGroupchatMarkerStanza = (
  client: Client,
  callback: (marker: Marker) => void,
  filter: (stanza: Element) => boolean
) => {
  return createStanzaListener(client, callback, element => {
    if (!filter(element)) {
      return
    }
    return parseArchivedGroupchatMarkerStanza(element)
  })
}

export const onGroupchatMarkerStanza = (client: Client, callback: (marker: Marker) => void) => {
  return createStanzaListener(client, callback, parseGroupchatMarkerStanza)
}

export const onConversationStatusChange = (
  client: Client,
  callback: (event: Extract<PayloadIn, { type: 'StatusChange' }>) => void
) => {
  return createStanzaListener(client, callback, parseConversationStatusChangeStanza)
}
